import React, { useRef, useState } from "react";
import {
  Button,
  TextField,
  Box,
  InputAdornment,
  Alert,
  IconButton,
  Chip,
} from "@mui/material";
import { Email as EmailIcon, Check as CheckIcon } from "@mui/icons-material";
import domtoimage from "dom-to-image";
import jsPDF from "jspdf";
import ProductSummary from "./ProductSummary";
import "./ProductExportLabel.css";
import shareIcon from "../../assets/new_product_detail/share-icon.svg";
import linkedinIcon from "../../assets/new_product_detail/linkedin-share.svg";
import whatsappIcon from "../../assets/new_product_detail/whatsapp-share.svg";
import mailIcon from "../../assets/new_product_detail/mail-share.svg";

const ProductExportLabel = ({ productData }) => {
  const componentRef = useRef(null);
  const [email, setEmail] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [exportFormat, setExportFormat] = useState("pdf"); // 'pdf' or 'image'
  const [exportBlob, setExportBlob] = useState(null);
  const [shareStatus, setShareStatus] = useState(false);

  const handleShare = (platform) => {
    // Handle sharing logic based on platform
    switch (platform) {
      case "general":
        // Use Web Share API or copy to clipboard
        break;
      case "email":
        // Open email client
        break;
      case "linkedin":
        // Open LinkedIn share
        break;
      case "whatsapp":
        // Open WhatsApp share
        break;
    }

    // Show success status
    setShareStatus(true);
    setTimeout(() => setShareStatus(false), 2000);
  };

  const showMessage = (text, type = "info") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(""), 5000);
  };

  const prepareComponentForExport = async () => {
    if (!componentRef.current) {
      throw new Error("Component reference not found");
    }

    const element = componentRef.current;

    // Ensure component is visible and fully rendered
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Apply export-specific styles for better rendering
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      .export-mode {
        transform: none !important;
        opacity: 1 !important;
        visibility: visible !important;
        position: static !important;
        overflow: visible !important;
      }
      
      .export-mode * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
        -webkit-font-smoothing: antialiased !important;
        -moz-osx-font-smoothing: grayscale !important;
      }
      
      .export-mode .MuiPaper-root {
        box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
        border: 1px solid #e0e0e0 !important;
        background-color: #ffffff !important;
      }
      
      .export-mode .MuiTypography-root {
        color: #000000 !important;
      }

      /* Ensure pseudo-elements are properly rendered */
      .export-mode *::before,
      .export-mode *::after {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      /* Force visibility of all child elements */
      .export-mode * {
        visibility: visible !important;
        opacity: 1 !important;
      }

      /* Ensure fonts are loaded */
      .export-mode {
        font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
      }
    `;

    document.head.appendChild(styleSheet);
    element.classList.add("export-mode");

    // Wait for styles to apply and fonts to load
    await new Promise((resolve) => setTimeout(resolve, 800));

    return () => {
      element.classList.remove("export-mode");
      document.head.removeChild(styleSheet);
    };
  };

  const exportAsPDF = async () => {
    const cleanup = await prepareComponentForExport();

    try {
      const element = componentRef.current;

      // Use dom-to-image to capture the element
      const dataUrl = await domtoimage.toPng(element, {
        quality: 1,
        bgcolor: "#ffffff",
        width: element.offsetWidth,
        height: element.offsetHeight,
        style: {
          transform: "scale(1)",
          transformOrigin: "top left",
          width: element.offsetWidth + "px",
          height: element.offsetHeight + "px",
        },
        filter: (node) => {
          // Filter out any elements that might cause issues
          if (node.classList && node.classList.contains("export-exclude")) {
            return false;
          }
          return true;
        },
        imagePlaceholder:
          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCAzMCAzMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiMxOTc2ZDIiLz4KPHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiBzdHlsZT0icG9zaXRpb246IGFic29sdXRlOyB0b3A6IDNweDsgbGVmdDogM3B4OyI+CjxwYXRoIGQ9Ik0xOSAzSDVjLTEuMSAwLTIgLjktMiAydjE0YzAgMS4xLjkgMiAyIDJoNGwzLTQgNC05IDQgOWgzYzEuMSAwIDItLjkgMi0yVjVjMC0xLjEtLjktMi0yLTJ6IiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4KPC9zdmc+",
      });

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const imageBlob = await response.blob();

      // Create image element to get dimensions
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = dataUrl;
      });

      // Create PDF
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (img.height * imgWidth) / img.width;

      const pdf = new jsPDF("p", "mm", "a4");

      // Add title
      pdf.setFontSize(16);
      pdf.setTextColor(32, 54, 105); // #203669
      pdf.text("Product Summary Report", 20, 20);

      // Add product name if available
      if (productData.product_name) {
        pdf.setFontSize(12);
        pdf.setTextColor(100, 100, 100);
        pdf.text(productData.product_name, 20, 30);
      }

      // Add the captured image
      if (imgHeight > pageHeight - 40) {
        // If image is too tall, split across multiple pages
        let yPosition = 40;
        let remainingHeight = imgHeight;
        let sourceY = 0;

        while (remainingHeight > 0) {
          const pageImageHeight = Math.min(
            remainingHeight,
            pageHeight - yPosition
          );

          // Create a canvas to crop the image for this page
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const sourceHeight = (pageImageHeight * img.height) / imgHeight;

          canvas.width = img.width;
          canvas.height = sourceHeight;

          ctx.drawImage(
            img,
            0,
            sourceY,
            img.width,
            sourceHeight,
            0,
            0,
            img.width,
            sourceHeight
          );

          const croppedDataUrl = canvas.toDataURL("image/png", 0.95);
          pdf.addImage(
            croppedDataUrl,
            "PNG",
            0,
            yPosition,
            imgWidth,
            pageImageHeight
          );

          remainingHeight -= pageImageHeight;
          sourceY += sourceHeight;

          if (remainingHeight > 0) {
            pdf.addPage();
            yPosition = 20;
          }
        }
      } else {
        pdf.addImage(dataUrl, "PNG", 0, 40, imgWidth, imgHeight);
      }

      // Add footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Generated on ${new Date().toLocaleDateString()} - Page ${i} of ${pageCount}`,
          20,
          pageHeight - 10
        );
      }

      return pdf.output("blob");
    } catch (error) {
      console.error("PDF export error:", error);
      throw new Error(`PDF export failed: ${error.message}`);
    } finally {
      cleanup();
    }
  };

  const exportAsImage = async () => {
    const cleanup = await prepareComponentForExport();

    try {
      const element = componentRef.current;

      console.log("Capturing element as image with dom-to-image");

      // Use dom-to-image to capture the element as PNG
      const dataUrl = await domtoimage.toPng(element, {
        quality: 1,
        bgcolor: "#ffffff",
        width: element.offsetWidth * 2, // Higher resolution
        height: element.offsetHeight * 2, // Higher resolution
        style: {
          transform: "scale(2)", // Scale up for better quality
          transformOrigin: "top left",
          width: element.offsetWidth + "px",
          height: element.offsetHeight + "px",
        },
        filter: (node) => {
          // Filter out any elements that might cause issues
          if (node.classList && node.classList.contains("export-exclude")) {
            return false;
          }
          return true;
        },
        imagePlaceholder:
          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCAzMCAzMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiMxOTc2ZDIiLz4KPHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiBzdHlsZT0icG9zaXRpb246IGFic29sdXRlOyB0b3A6IDNweDsgbGVmdDogM3B4OyI+CjxwYXRoIGQ9Ik0xOSAzSDVjLTEuMSAwLTIgLjktMiAydjE0YzAgMS4xLjkgMiAyIDJoNGwzLTQgNC05IDQgOWgzYzEuMSAwIDItLjkgMi0yVjVjMC0xLjEtLjktMi0yLTJ6IiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4KPC9zdmc+",
      });

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      return await response.blob();
    } catch (error) {
      console.error("Image export error:", error);
      throw new Error(`Image export failed: ${error.message}`);
    } finally {
      cleanup();
    }
  };

  const handleExport = async () => {
    if (!productData) {
      showMessage("No product data available", "error");
      return;
    }

    setIsExporting(true);

    try {
      showMessage(`Creating ${exportFormat.toUpperCase()}...`, "info");

      let blob;
      if (exportFormat === "pdf") {
        blob = await exportAsPDF();
      } else {
        blob = await exportAsImage();
      }

      if (!blob) {
        showMessage(`Failed to create ${exportFormat.toUpperCase()}`, "error");
        return;
      }

      setExportBlob(blob);

      // Download the file
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const extension = exportFormat === "pdf" ? "pdf" : "png";
      const fileName = `product-summary-${
        productData.product_num || Date.now()
      }.${extension}`;

      link.href = url;
      link.download = fileName;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 100);
      showMessage(
        `${exportFormat.toUpperCase()} downloaded successfully!`,
        "success"
      );
    } catch (error) {
      console.error("Export error:", error);
      showMessage(`Export failed: ${error.message}`, "error");
    } finally {
      setIsExporting(false);
    }
  };

  const sendEmail = async () => {
    if (!email.trim()) {
      showMessage("Please enter an email address.", "error");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showMessage("Please enter a valid email address.", "error");
      return;
    }

    if (!productData) {
      showMessage("No product data available", "error");
      return;
    }

    try {
      let blob = exportBlob;
      if (!blob) {
        showMessage(
          `Creating ${exportFormat.toUpperCase()} for email...`,
          "info"
        );
        if (exportFormat === "pdf") {
          blob = await exportAsPDF();
        } else {
          blob = await exportAsImage();
        }
        if (!blob) {
          showMessage(
            `Failed to create ${exportFormat.toUpperCase()} for email`,
            "error"
          );
          return;
        }
        setExportBlob(blob);
      }

      const subject = `Product Summary - ${
        productData.product_name || "Product"
      }`;
      const extension = exportFormat === "pdf" ? "pdf" : "png";
      const mimeType = exportFormat === "pdf" ? "application/pdf" : "image/png";

      const body = `Dear Recipient,

Please find the product summary details attached.

Product Information:
• Name: ${productData.product_name || "N/A"}
• Product Number: ${productData.product_num || "N/A"}
• Country of Origin: ${productData.country_origin || "N/A"}
• CO2e: ${productData.total_prod_co2?.toFixed(2) || "N/A"} kg

Best regards,
YFS Team`;

      // Try Web Share API first
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], `product-summary.${extension}`, {
            type: mimeType,
          });

          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: subject,
              text: body,
              files: [file],
            });
            showMessage("Shared successfully!", "success");
            return;
          }
        } catch (shareError) {
          console.log("Web Share failed, falling back to email");
        }
      }

      // Create .eml file with attachment
      const reader = new FileReader();
      reader.onload = function (e) {
        const base64Data = e.target.result.split(",")[1];

        const emlContent = `To: ${email}
Subject: ${subject}
MIME-Version: 1.0
Content-Type: multipart/mixed; boundary="boundary123"

--boundary123
Content-Type: text/plain; charset=utf-8
Content-Transfer-Encoding: 7bit

${body}

--boundary123
Content-Type: ${mimeType}
Content-Transfer-Encoding: base64
Content-Disposition: attachment; filename="product-summary.${extension}"

${base64Data}

--boundary123--`;

        const emlBlob = new Blob([emlContent], { type: "message/rfc822" });
        const emlUrl = URL.createObjectURL(emlBlob);
        const emlLink = document.createElement("a");
        emlLink.href = emlUrl;
        emlLink.download = `product-summary-email-${Date.now()}.eml`;
        emlLink.style.display = "none";
        document.body.appendChild(emlLink);
        emlLink.click();
        document.body.removeChild(emlLink);
        setTimeout(() => URL.revokeObjectURL(emlUrl), 100);

        // Open default email client
        const mailtoLink = `mailto:${encodeURIComponent(
          email
        )}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
          body
        )}`;

        try {
          window.location.href = mailtoLink;
          showMessage(
            "Email client opened and email file downloaded with attachment!",
            "success"
          );
        } catch (e) {
          showMessage(
            "Email file with attachment downloaded! Check your downloads folder.",
            "success"
          );
        }
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      console.error("Email error:", error);
      showMessage(`Email error: ${error.message}`, "error");
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      {/* Product Summary Component */}
      <Box
        ref={componentRef}
        sx={{
          mb: 4,
          position: "relative",
          backgroundColor: "#ffffff",
          padding: 3,
          borderRadius: 1,
          border: "1px solid #e0e0e0",
          minHeight: "400px",
          width: "100%",
          overflow: "visible",
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: "14px",
          lineHeight: 1.5,
        }}
      >
        <ProductSummary productData={productData} editMode={false} />
      </Box>

      {/* Export Controls */}
      <div
        style={{ padding: "24px", marginTop: "64px" }}
        className="product-export-label-bg"
      >
        <Box sx={{ position: "absolute", top: "-15px", left: "45%" }}>
          <img src={shareIcon} alt="Share icon" width={56} height={56} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Format Selection */}
          <Box sx={{ mt: 5 }}>
            <div className="export-options-bg">
              <button
                className={`export-option ${
                  exportFormat === "pdf" ? "active" : ""
                }`}
                onClick={() => {
                  setExportFormat("pdf");
                  setExportBlob(null);
                }}
              >
                PDF (Recommended)
              </button>
              <button
                className={`export-option ${
                  exportFormat === "image" ? "active" : ""
                }`}
                onClick={() => {
                  setExportFormat("image");
                  setExportBlob(null);
                }}
              >
                PNG Image
              </button>
            </div>
          </Box>

          {/* Download Button */}
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="product-export-download"
          >
            {isExporting ? `Creating` : `Download `}
          </button>

          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 2, my: 2 }}
          >
            <IconButton
              onClick={() => handleShare("general")}
              sx={{
                color: "white",
                width: 48,
                height: 48,
              }}
            >
              <img src={shareIcon} alt="Share" width={24} height={24} />
            </IconButton>
            <IconButton
              onClick={() => handleShare("email")}
              sx={{
                color: "white",
                width: 48,
                height: 48,
              }}
            >
              <img src={mailIcon} alt="Email" width={24} height={24} />
            </IconButton>
            <IconButton
              onClick={() => handleShare("linkedin")}
              sx={{
                color: "white",
                width: 48,
                height: 48,
              }}
            >
              <img src={linkedinIcon} alt="LinkedIn" width={24} height={24} />
            </IconButton>
            <IconButton
              onClick={() => handleShare("whatsapp")}
              sx={{
                color: "white",
                width: 48,
                height: 48,
              }}
            >
              <img src={whatsappIcon} alt="WhatsApp" width={24} height={24} />
            </IconButton>
          </Box>

          {/* Status Badge */}
          {shareStatus && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Chip
                label="Sent"
                icon={<CheckIcon />}
                color="success"
                variant="filled"
                sx={{
                  backgroundColor: "#4a5568",
                  color: "white",
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
            </Box>
          )}

          {/* Email Section */}
          <TextField
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Type email address here"
            size="medium"
            className="product-export-download"
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box
                    onClick={() => {
                      if (!isExporting && email.trim()) sendEmail();
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor:
                        isExporting || !email.trim()
                          ? "not-allowed"
                          : "pointer",
                      opacity: isExporting || !email.trim() ? 0.4 : 1,
                      gap: 0.5,
                      pr: 1,
                    }}
                  >
                    <span style={{ color: "#1B3764", fontWeight: 500 }}>
                      {shareStatus ? "Sent" : "Send"}
                    </span>
                    <CheckIcon
                      sx={{
                        fontSize: 18,
                        color: "#1B3764",
                        opacity: shareStatus ? 1 : 0.6,
                        transition: "opacity 0.3s ease-in-out",
                      }}
                    />
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#1B3764",
                "& input::placeholder": {
                  color: "#1B3764",
                  opacity: 0.6,
                },
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
            }}
          />

          {/* Status Message */}
          {message && (
            <Alert
              severity={messageType}
              onClose={() => setMessage("")}
              sx={{ mt: 1 }}
            >
              {message}
            </Alert>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default ProductExportLabel;
